/**
 * Utilities for comparing HTML content that may have been normalized by rich text editors (like Quill).
 *
 * Rich text editors often normalize HTML on load, causing differences like:
 * - Whitespace before closing tags: "text </p>" → "text</p>"
 * - Ampersand encoding: "&" → "&amp;"
 *
 * These utilities help detect actual user changes vs editor normalization.
 */

/**
 * Normalizes HTML to handle common editor transformations.
 * Use this when comparing HTML values to detect actual changes.
 */
export const normalizeHtmlForComparison = (html: string | undefined): string => {
  if (!html) return '';
  return html
    .replace(/\s+<\//g, '</') // Remove whitespace before closing tags
    .replace(/&(?![a-zA-Z]+;|#[0-9]+;|#x[0-9a-fA-F]+;)/g, '&amp;') // Normalize unencoded ampersands
    .replace(/^<p>([^]*)<\/p>$/i, (match, inner: string) => {
      // Only strip outer <p> wrapper if content is a single paragraph (no nested block tags)
      return inner.includes('<p>') || inner.includes('</p>') ? match : inner;
    });
};

/**
 * Compares two HTML strings, accounting for editor normalization.
 * Returns true if the content is effectively the same.
 */
export const isHtmlEqual = (a: string | undefined, b: string | undefined): boolean => {
  return normalizeHtmlForComparison(a) === normalizeHtmlForComparison(b);
};

/**
 * Checks if an HTML field has actually changed (not just normalized by editor).
 */
export const hasHtmlFieldChanged = (
  initial: string | undefined,
  current: string | undefined,
): boolean => {
  return !isHtmlEqual(initial, current);
};

/** Configuration for a field that can be compared */
export interface FieldConfig {
  label: string;
  isHtml: boolean;
}

/**
 * Checks if a single field has changed, using HTML comparison for HTML fields.
 */
export const hasFieldChanged = (
  initial: string | undefined,
  current: string | undefined,
  isHtml: boolean,
): boolean => {
  return isHtml ? hasHtmlFieldChanged(initial, current) : initial !== current;
};

/**
 * Gets labels of changed fields by comparing initial and current values.
 * @param fields - Record of field names to their config (label and isHtml flag)
 * @param getInitial - Function to get initial value for a field
 * @param getCurrent - Function to get current value for a field
 * @param skipFields - Optional set of field names to skip
 */
export const getChangedFields = (
  fields: Record<string, FieldConfig>,
  getInitial: (field: string) => string | undefined,
  getCurrent: (field: string) => string | undefined,
  skipFields?: Set<string>,
): string[] => {
  const labels: string[] = [];

  for (const [field, config] of Object.entries(fields)) {
    if (skipFields?.has(field)) continue;

    if (hasFieldChanged(getInitial(field), getCurrent(field), config.isHtml)) {
      labels.push(config.label);
    }
  }

  return labels;
};
